/**
 * Persona cast — shared logic for the demo persona switcher (now hosted inside
 * the Mode switcher dropdown; effects run via usePersonaReconcile).
 *
 * Split out of the old PersonaSwitcher so two consumers can share one source:
 *   - usePersonaReconcile  — the always-on refresh-restore + company/persona
 *                            alignment effects (mounted in the Header).
 *   - PersonaPicker        — the picker UI rendered in the Demo ▾ dropdown.
 *
 * Role resolution: the deployed demo is anon and `users` RLS is authenticated-
 * only, so system_role can't be read; elevated roles are matched by id/pernr
 * here (admin persona -> OrgAdmin, the featured manager/auditor by id, everyone
 * else Staff), mirroring the seeded data.
 */
import { useAppStore, PERSIST_KEY_DEMO_PERSONA } from '@/store/appStore'
import { useTimesheetStore } from '@/modules/timesheet/stores/timesheetStore'
import { bootstrapReferenceData, timesheetDataService } from '@/modules/timesheet/services/timesheetDataService'
import { getDemoCompanyByCastOrg } from '@/types/demoCompanies'
import type { TsEmployee } from '@/modules/timesheet/types/timesheet.types'

// The compliance-oversight persona (Dr. Linda Nakamura) is featured as the
// read-only Auditor so the demo exercises all four featured roles. Matched on the
// canonical pernr (carried in both mock and supabase) plus the supabase row id.
export const AUDITOR_IDS = new Set(['ts-emp-004', 'user_004', 'ec-emp-aud-001', 'user_ec_audit', 'bo-emp-aud-001', 'user_bo_audit', 'codan-emp-03', 'user_codan_03',
  // Mach12 cast (per-company one-of-each buildout, migration 00421). Colgate
  // ids removed when the Colgate demo pack was retired (Kelly Behringer stays).
  'user_wzfalnnx'])
// The single Manager persona (Maria Santos) — drives the approvals demo.
export const MANAGER_IDS = new Set(['ts-emp-002', 'user_002', 'ec-emp-mgr-001', 'user_ec_mgr', 'bo-emp-mgr-001', 'user_bo_mgr', 'codan-emp-02', 'user_codan_02',
  // Mach12 cast (per-company one-of-each buildout, migration 00421). Colgate
  // ids removed when the Colgate demo pack was retired (Jay Wilson stays).
  'user_ekmxkcgm'])

/** Access role for the override — mirrors the seeded users.system_role. */
export function deriveSystemRole(emp: TsEmployee): string {
  // Match the all-access admin persona by its 'all access' marker only (a bare
  // 'admin' substring also catches ordinary job titles). The demo admin is a
  // customer-company admin → OrgAdmin; GlobalAdmin is the internal-only platform dev.
  if (emp.role?.toLowerCase().includes('all access')) return 'OrgAdmin'
  if (AUDITOR_IDS.has(emp.id) || (emp.pernr != null && AUDITOR_IDS.has(emp.pernr))) return 'Auditor'
  if (MANAGER_IDS.has(emp.id) || (emp.pernr != null && MANAGER_IDS.has(emp.pernr))) return 'Supervisor'
  return 'Staff' // individual contributors (incl. subcontractors + manufacturing workers)
}

/** App-wide RBAC role to set on currentUser.role. */
export function deriveAppRole(systemRole: string): string {
  if (systemRole === 'GlobalAdmin' || systemRole === 'OrgAdmin') return 'GlobalAdmin'
  if (systemRole === 'Staff') return 'Viewer'
  if (systemRole === 'Auditor') return 'Viewer' // read-only oversight
  return 'PortfolioManager'
}

/** Worker-TYPE sections for the grouped picker. */
export const SECTIONS: readonly { key: string; label: string; hint: string }[] = [
  { key: 'hourly',     label: 'Hourly',                hint: 'Office hourly · grid + schedule check' },
  { key: 'salary',     label: 'Salary',                hint: 'Office salaried · timesheet grid' },
  { key: 'mfg-hourly', label: 'Manufacturing Hourly',  hint: 'Shop floor · Clock In/Out + Mfg Day' },
  { key: 'mfg-salary', label: 'Manufacturing Salary',  hint: 'Salaried floor · grid + floor view' },
  { key: 'general',    label: 'General Salary',        hint: 'One charge code daily · auto-fill' },
  { key: 'sub',        label: 'Subcontractor',         hint: 'External · sub time portal' },
  { key: 'manager',    label: 'Manager',               hint: 'Approves the team' },
  { key: 'auditor',    label: 'Auditor',               hint: 'Read-only oversight' },
  { key: 'admin',      label: 'Admin',                 hint: 'Full access' },
]

/** Which section a persona belongs to — access role wins, else bucket the IC. */
export function workerSection(emp: TsEmployee): string {
  const sys = deriveSystemRole(emp)
  if (sys === 'GlobalAdmin' || sys === 'OrgAdmin') return 'admin'
  if (sys === 'Auditor') return 'auditor'
  if (sys === 'Supervisor') return 'manager'
  if (emp.payClass === 'subcontractor') return 'sub'
  if (emp.payClass === 'general-time') return 'general'
  const mfg = emp.workCategory === 'manufacturing'
  if (emp.payClass === 'hourly') return mfg ? 'mfg-hourly' : 'hourly'
  return mfg ? 'mfg-salary' : 'salary'
}

/**
 * Ensure the persona roster is loaded (idempotent), then re-sync the active
 * persona once the real rows land. Historically only the timesheet routes
 * provider ran this, so the switcher showed the stale mock seed cast (the
 * ex-base cast that is now Gentex's) on PPM pages until a timesheet page was
 * visited. Now also called from the shell (usePersonaReconcile) so the
 * switcher scopes to the active company's real cast on every page.
 *
 * After the roster lands: bump referenceDataVersion so subscribers (picker,
 * Sidebar, route trees) re-render, then re-resolve the active persona's
 * name/initials — the chrome identity is restored from a localStorage snapshot
 * that can carry a stale mock seed name captured before the cache warmed — and
 * keep the persisted snapshot in sync so the next refresh is already correct.
 */
export async function ensurePersonaRosterLoaded(): Promise<void> {
  try {
    await bootstrapReferenceData()
  } catch (err) {
    console.error('[personas] bootstrapReferenceData failed', err)
    return
  }
  useAppStore.getState().bumpReferenceDataVersion()
  const u = useAppStore.getState().currentUser
  if (!u?.id) return
  // getEmployee resolves by id OR pernr, so it works whether the snapshot
  // stored user_001 or ts-emp-001.
  const emp = timesheetDataService.getEmployee(u.id)
  if (!emp) return
  if (emp.name === u.name && emp.initials === u.initials) return
  useAppStore.getState().setCurrentUser({ ...u, name: emp.name, initials: emp.initials })
  try {
    const raw = localStorage.getItem(PERSIST_KEY_DEMO_PERSONA)
    if (raw) {
      const snap = JSON.parse(raw) as Record<string, unknown>
      localStorage.setItem(
        PERSIST_KEY_DEMO_PERSONA,
        JSON.stringify({ ...snap, name: emp.name, initials: emp.initials }),
      )
    }
  } catch { /* ignore disabled storage */ }
}

/** Persist the full persona snapshot so a refresh restores the identity immediately. */
export function persistPersonaSnapshot(emp: TsEmployee, systemRole: string, appRole: string) {
  try {
    localStorage.setItem(PERSIST_KEY_DEMO_PERSONA, JSON.stringify({
      id: emp.id, name: emp.name, initials: emp.initials, appRole, systemRole,
    }))
  } catch { /* ignore disabled storage */ }
}

/** Short role/pay tag, same vocabulary as the Settings picker. */
export function personaTag(emp: TsEmployee): string {
  if (emp.payClass === 'subcontractor') return emp.isSubLead ? 'Subcontractor · lead' : 'Subcontractor'
  if (emp.role?.toLowerCase().includes('all access')) return 'All access · admin'
  if (emp.payClass === 'general-time') return 'General-time'
  if (emp.scheduleType === '9x80') return 'Salary · 9/80'
  if (emp.scheduleType === '4x10') return 'Hourly · 4/10'
  if (emp.payClass === 'hourly') return 'Hourly · 5/8'
  if (emp.payClass === 'salary') return 'Salary · 5/8'
  return emp.role ?? ''
}

/**
 * Apply a persona = set the whole profile (chrome identity + access override +
 * active timesheet employee + refresh-restore snapshot + company alignment).
 * Pass `opts.navigate` (a navigate fn) to also land on the timesheet.
 */
export function applyPersona(emp: TsEmployee, opts?: { navigate?: (path: string) => void }) {
  const { setCurrentUser, setTimesheetRoleOverride, setCurrentCompany, currentUser, currentCompanyId } = useAppStore.getState()
  const systemRole = deriveSystemRole(emp)
  const appRole = deriveAppRole(systemRole)
  setCurrentUser({ ...currentUser, id: emp.id, name: emp.name, role: appRole as typeof currentUser.role, initials: emp.initials })
  setTimesheetRoleOverride(systemRole)
  useTimesheetStore.getState().setCurrentEmployeeId(emp.id)
  persistPersonaSnapshot(emp, systemRole, appRole)
  // Keep the active company aligned with the persona's cast company, so a refresh
  // restores a consistent (company + persona) pair.
  const personaCompany = getDemoCompanyByCastOrg(emp.primaryOrganizationId)
  if (personaCompany && personaCompany.id !== currentCompanyId) {
    setCurrentCompany(personaCompany.id)
  }
  if (opts?.navigate) {
    useAppStore.getState().setManagementContext('timesheet')
    opts.navigate('/timesheet')
  }
}
